import { type JSX, type ReactNode } from "react";

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { CloseButton } from "@vega-core/components/Buttons/CloseButton/CloseButton";
import { PageSubTitle } from "@vega-core/components/Headers/PageSubTitle";
import { DESKTOP_MODAL_MIN_WIDTH } from "@vega-core/components/Modals/Modals.constants";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { useIgnoreScrollbarClose } from "@vega-core/hooks/useIgnoreScrollbarClose";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

type Props = {
  children: JSX.Element | ReactNode | string;
  dataTestId?: string;
  fullScreen?: boolean;
  hideCloseButton?: boolean;
  ignoreMinWidth?: boolean;
  onClose?: VoidFunction;
  onTransitionEnd?: VoidFunction;
  panelClassName?: string;
  open: boolean;
  setOpen?: (open: boolean) => void;
  theme?: string;
  title?: string | null;
};

export function VegaDialog({
  children,
  dataTestId,
  fullScreen,
  hideCloseButton,
  onClose,
  onTransitionEnd,
  panelClassName,
  open,
  setOpen,
  theme,
  title,
}: Props) {
  const close = () => {
    onClose && onClose();
    setOpen && setOpen(false);
  };
  const handleClose = useIgnoreScrollbarClose({ open, onClose: close });
  const isDesktop = useDeviceWidth("md");

  return (
    <Transition afterLeave={onTransitionEnd} show={open}>
      <Dialog
        aria-label="modal"
        as="div"
        className={cnTwMerge("relative z-modal text-text/primary", theme)}
        data-testid={dataTestId ?? "vega-dialog"}
        onClose={handleClose}
      >
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-background/secondary/[0.80] blur-sm transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto" role="modal">
          <div
            className={cnTwMerge(
              "flex items-center justify-center min-h-full p-2 sm:items-center",
              theme,
            )}
          >
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={cnTwMerge(
                  "relative grow transform rounded-companyBorderRadius outline outline-border/secondary bg-background/primary p-6 shadow-xl transition-all md:grow-0 md:p-8 flex-1",
                  theme,
                  isTruthy(fullScreen)
                    ? "fixed inset-0 z-[100]"
                    : (panelClassName ?? "sm:max-w-lg"),
                )}
                style={{
                  minWidth: isDesktop ? DESKTOP_MODAL_MIN_WIDTH : "unset",
                }}
              >
                {isTruthy(title) ? (
                  <DialogTitle className="mr-4">
                    <PageSubTitle>{title}</PageSubTitle>
                  </DialogTitle>
                ) : null}
                {isTruthy(hideCloseButton) ? null : (
                  <div className="absolute right-4 top-4">
                    <CloseButton onClick={close} />
                  </div>
                )}
                <div>{children}</div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
